import { useMemo, useState } from "react";
import {
  Alert,
  Checkbox,
  Input,
  Modal,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  ApiError,
  type BatchItemResult,
  type CorrectionReasonRow,
  type TransactionRow,
  api,
} from "../api/client";
import { formatCents, formatCompactDateTime } from "../utils/dateFormat";
import type { PendingBatch } from "../hooks/usePendingBatch";

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Triggered after a *fully* successful batch — caller clears state +
   * refetches. Partial-success keeps the modal open so the operator can
   * see + retry failed rows. */
  onComplete: () => void;
  pending: PendingBatch;
  reasons: CorrectionReasonRow[];
  transactions: TransactionRow[];
  currentBalance: number | null;
}

interface PreviewRow {
  txId: number;
  created: string;
  amount: number;
  article: string | null;
  reasonId: number;
  reasonLabel: string;
}

/**
 * Two-state modal:
 *
 *  1. **Review** — operator sees a table of every row in the batch with
 *     its picked reason, total signed amount, projected new balance,
 *     and a free-text batch comment field. Confirms with a button.
 *
 *  2. **Results** — after Confirm, the modal flips to show the per-row
 *     status (completed / failed / validation_failed). Cancel becomes
 *     "Close"; on all-success the caller's onComplete fires.
 */
export const CorrectionsBatchReviewModal = ({
  open,
  onClose,
  onComplete,
  pending,
  reasons,
  transactions,
  currentBalance,
}: Props) => {
  const [batchComment, setBatchComment] = useState("");
  const [returnToStock, setReturnToStock] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<BatchItemResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preview = useMemo<PreviewRow[]>(() => {
    const rs = new Map(reasons.map((r) => [r.id, r]));
    const txs = new Map(transactions.map((t) => [t.id, t]));
    const out: PreviewRow[] = [];
    pending.pending.forEach((reasonId, txId) => {
      const tx = txs.get(txId);
      const reason = rs.get(reasonId);
      if (!tx || !reason) return;
      out.push({
        txId,
        created: tx.created,
        amount: tx.amount,
        article: tx.article_name,
        reasonId,
        reasonLabel: reason.label,
      });
    });
    return out.sort((a, b) => +new Date(b.created) - +new Date(a.created));
  }, [pending.pending, reasons, transactions]);

  const totalReversed = preview.reduce((acc, r) => acc + r.amount, 0);
  // Only article purchases can return to stock; deposits/credits/transfers
  // carry no article. Gate the checkbox on there being at least one.
  const articleRowCount = preview.filter((r) => r.article).length;
  // The counter-transfer flips the sign on the user's balance, so the
  // projected new balance after applying every correction is:
  //   new = current - amount_summed_in_history
  // because each correction adds back -amount to the user's running total.
  const projectedNewBalance =
    currentBalance === null ? null : currentBalance - totalReversed;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.commitCorrectionBatch({
        batch_comment: batchComment.trim() || undefined,
        return_to_stock: returnToStock,
        items: preview.map((p) => ({
          original_transaction_id: p.txId,
          reason_id: p.reasonId,
        })),
      });
      setResults(res.items);
      // Remove successful rows from the pending map so the operator can
      // see only the leftovers to retry. Keep failed rows IN pending so
      // their reason picks aren't lost.
      res.items.forEach((it) => {
        if (it.status === "completed") {
          pending.remove(it.original_transaction_id);
        }
      });
      if (res.items.every((it) => it.status === "completed")) {
        // Schedule the close so the operator briefly sees "Done" before
        // the parent refetches and the modal unmounts.
        window.setTimeout(() => onComplete(), 400);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reviewColumns: ColumnsType<PreviewRow> = [
    {
      title: "When",
      dataIndex: "created",
      key: "created",
      render: (v: string) => formatCompactDateTime(v),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (v: number) => (
        <Text type={v < 0 ? "danger" : "success"}>{formatCents(v)}</Text>
      ),
    },
    {
      title: "Article",
      dataIndex: "article",
      key: "article",
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "Reason",
      dataIndex: "reasonLabel",
      key: "reasonLabel",
    },
  ];

  const resultColumns: ColumnsType<BatchItemResult> = [
    {
      title: "Original tx",
      dataIndex: "original_transaction_id",
      key: "tx",
      render: (v: number) => `#${v}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: BatchItemResult["status"]) =>
        v === "completed" ? (
          <Tag color="green">Completed</Tag>
        ) : v === "failed" ? (
          <Tag color="red">Failed</Tag>
        ) : (
          <Tag color="orange">Validation</Tag>
        ),
    },
    {
      title: "Receipt",
      dataIndex: "receipt_no",
      key: "receipt",
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
      render: (v: string | null) => v ?? "",
    },
  ];

  return (
    <Modal
      open={open}
      title={results ? "Correction results" : `Review ${preview.length} correction${preview.length === 1 ? "" : "s"}`}
      onCancel={onClose}
      onOk={handleConfirm}
      okText="Confirm batch"
      okButtonProps={{ disabled: !!results, loading: submitting }}
      cancelText={results ? "Close" : "Cancel"}
      width={720}
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      {!results ? (
        <>
          <Table<PreviewRow>
            columns={reviewColumns}
            dataSource={preview}
            rowKey="txId"
            pagination={false}
            size="small"
            sticky
          />
          <Space style={{ marginTop: 16 }} size="large">
            <Statistic
              title="Total reversed"
              value={formatCents(totalReversed)}
              valueStyle={{ fontSize: 16 }}
            />
            <Statistic
              title="Projected new balance"
              value={
                projectedNewBalance === null
                  ? "—"
                  : formatCents(projectedNewBalance)
              }
              valueStyle={{ fontSize: 16 }}
            />
          </Space>
          <div style={{ marginTop: 16 }}>
            <Typography.Text strong>Batch comment (optional)</Typography.Text>
            <Input.TextArea
              rows={2}
              value={batchComment}
              onChange={(e) => setBatchComment(e.target.value)}
              placeholder="Context shared across every row in this batch…"
              style={{ marginTop: 4 }}
            />
          </div>
          <div style={{ marginTop: 16 }}>
            <Checkbox
              checked={returnToStock}
              disabled={articleRowCount === 0}
              onChange={(e) => setReturnToStock(e.target.checked)}
            >
              Return item(s) to stock
            </Checkbox>
            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: 12, marginTop: 4, marginBottom: 0 }}
            >
              {articleRowCount === 0
                ? "No article purchases in this batch — nothing to return."
                : `Adds the bought unit(s) back to expected stock for the ${articleRowCount} article purchase${
                    articleRowCount === 1 ? "" : "s"
                  } here. Leave off for a money-only refund (the goods were consumed).`}
            </Typography.Paragraph>
          </div>
        </>
      ) : (
        <Table<BatchItemResult>
          columns={resultColumns}
          dataSource={results}
          rowKey="original_transaction_id"
          pagination={false}
          size="small"
        />
      )}
    </Modal>
  );
};
